import * as Sentry from '@sentry/react';
import { call, put } from 'redux-saga/effects';
import { setError } from '../store/appSlice';

export interface FetchOptions extends RequestInit {
  parseJSON?: boolean;
  parseErrorJSON?: boolean;
  throwError?: boolean;
  errorMessage?: string;
  inlineError?: boolean;
  expectedStatus?: number[];
  /** Picks out failures that aren't bugs, given the parsed error body. */
  knownError?: (body: object | null) => KnownError | null;
}

/**
 * A failure that's an expected outcome rather than a bug, e.g. a declined card.
 * It's still reported, but as a warning under its own Sentry issue.
 */
export interface KnownError {
  /** Title of the Sentry issue that groups every failure of this kind. */
  title: string;
  tags?: Record<string, string>;
}

export class ApiError extends Error {
  /** HTTP status code of the failed response. */
  code: number;
  /** Parsed JSON error payload when the server sent one, else the raw text. */
  body?: unknown;

  constructor(message: string, code: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.body = body;
  }
}

const defaultFetchOptions: FetchOptions = {
  parseJSON: true,
  parseErrorJSON: false,
  credentials: 'include',
  throwError: false,
};

function parseBody(response: Response, raw: string): unknown {
  if (!raw) return null;
  const contentType = response.headers.get('content-type') || '';
  if (contentType.toLowerCase().includes('json')) {
    try {
      return JSON.parse(raw);
    } catch {
      // Advertised JSON but wasn't — fall back to the raw text below.
    }
  }
  return raw;
}

function parseErrorBody(raw: string): object | null {
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function endpointFor(url: string, method = 'GET'): string {
  let path = url;
  try {
    path = new URL(url, window.location.origin).pathname.replace(
      /\/(catalog|cart\/product)\/[^/]+/,
      '/$1/:id',
    );
  } catch {
    // Unparseable — keep the raw url. This runs while reporting a failed
    // fetch, and reporting must not throw.
  }
  return `${method.toUpperCase()} ${path}`;
}

const api = {
  *fetch(
    url: string,
    options: FetchOptions = {},
  ): Generator<unknown, unknown, unknown> {
    const {
      parseJSON,
      parseErrorJSON,
      throwError,
      errorMessage,
      inlineError,
      expectedStatus,
      knownError,
      ...request
    } = {
      ...defaultFetchOptions,
      ...options,
    };

    let response: Response;
    try {
      response = (yield call(fetch, url, request)) as Response;
    } catch (e) {
      const endpoint = endpointFor(url, request.method);
      // No response at all. Offline, it's surely the visitor's connection;
      // online, it could also be our server being unreachable.
      Sentry.captureException(
        new Error(`Api call got no response: ${endpoint}`, { cause: e }),
        {
          // Each browser words and traces this failure differently, which
          // split it into many issues, so group by endpoint instead.
          fingerprint: ['api-call-no-response', endpoint],
          level: navigator.onLine ? 'error' : 'warning',
          tags: {
            network: 'true',
            online: String(navigator.onLine),
            endpoint,
          },
          extra: { url },
        },
      );
      if (!inlineError) {
        yield put(setError(errorMessage || ''));
      }
      return null;
    }

    if (response.status >= 400) {
      const raw = (yield response.text()) as string;
      const expected = expectedStatus?.includes(response.status) || false;

      if (!expected) {
        const endpoint = endpointFor(url, request.method);
        const known = knownError?.(parseErrorBody(raw));
        Sentry.captureException(
          new Error(
            known?.title ??
              `Api call failed with ${response.status}: ${endpoint}`,
          ),
          {
            // Every failure is thrown from this same line, so Sentry's default
            // stack-trace grouping lumps all statuses into one issue.
            fingerprint: known
              ? ['known-error', known.title]
              : ['api-call-failed', String(response.status), endpoint],
            level: known ? 'warning' : 'error',
            tags: {
              'http.status_code': response.status,
              endpoint,
              ...known?.tags,
            },
            extra: {
              url,
              request,
              response: raw,
            },
          },
        );
      }

      if (throwError) {
        throw new ApiError(
          errorMessage || `Api call failed with ${response.status}`,
          response.status,
          parseBody(response, raw),
        );
      }

      if (!inlineError && !expected) {
        yield put(setError(errorMessage || ''));
      }

      if (parseErrorJSON) {
        const body = parseErrorBody(raw);
        if (body) {
          return body;
        }
      }

      return null;
    }

    if (!parseJSON) {
      return response;
    }

    try {
      return yield response.json();
    } catch (e) {
      if (throwError) {
        throw new ApiError((e as Error).message, response.status);
      }
      if (!inlineError) {
        yield put(setError(errorMessage || ''));
      }
      return null;
    }
  },
};

export default api;
